export const WEDDING_CLASSIC_YEAR_CUTOFF = 1990;
export const WEDDING_MIN_VALID_YEAR = 1886;
export const weddingMaxValidYear = (): number => new Date().getUTCFullYear() + 1;

const WEDDING_PRESTIGE_MAKES = new Set([
  "bentley",
  "mercedesbenz",
  "rollsroyce",
]);

export interface WeddingSuitabilityCar {
  year: number | null | undefined;
  make: string | null | undefined;
  rentalEventActive: boolean | null | undefined;
  rentalChauffeuredActive: boolean | null | undefined;
  priceEvent?: number | null;
  eventMinPrice?: number | null;
  eventHourlyRate?: number | null;
  chauffeuredMinPrice?: number | null;
  chauffeuredHourlyRate?: number | null;
  minimumPrice?: number | null;
}

export type WeddingPriceUnit = "day" | "hour" | "minimum";

export interface WeddingDisplayPrice {
  price: number;
  unit: WeddingPriceUnit;
  hourlyPrice: number | null;
}

export function normalizeWeddingMake(make: string | null | undefined): string {
  return (make || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
}

export function isWeddingVehicleYearEligible(
  car: Pick<WeddingSuitabilityCar, "year" | "make">,
): boolean {
  if (
    !Number.isInteger(car.year) ||
    car.year! < WEDDING_MIN_VALID_YEAR ||
    car.year! > weddingMaxValidYear()
  ) return false;
  return car.year! < WEDDING_CLASSIC_YEAR_CUTOFF ||
    WEDDING_PRESTIGE_MAKES.has(normalizeWeddingMake(car.make));
}

const positive = (value: number | null | undefined): value is number =>
  typeof value === "number" && Number.isFinite(value) && value > 0;

/**
 * Resolves a wedding-safe price without guessing a period. Explicit booking
 * minimums win, followed by explicit hourly rates, then the legacy event daily
 * field. This keeps mixed legacy rows from advertising an ambiguous low
 * `price_event` as a day rate when better event/chauffeur semantics exist.
 */
export function getWeddingDisplayPrice(
  car: WeddingSuitabilityCar,
): WeddingDisplayPrice | null {
  const minimums: number[] = [];
  if (car.rentalEventActive && positive(car.eventMinPrice)) minimums.push(car.eventMinPrice);
  if (car.rentalChauffeuredActive && positive(car.chauffeuredMinPrice)) minimums.push(car.chauffeuredMinPrice);
  if ((car.rentalEventActive || car.rentalChauffeuredActive) && positive(car.minimumPrice)) {
    minimums.push(car.minimumPrice);
  }

  const hourly: number[] = [];
  if (car.rentalEventActive && positive(car.eventHourlyRate)) hourly.push(car.eventHourlyRate);
  if (car.rentalChauffeuredActive && positive(car.chauffeuredHourlyRate)) {
    hourly.push(car.chauffeuredHourlyRate);
  }
  const lowestHourly = hourly.length ? Math.min(...hourly) : null;

  if (minimums.length) {
    return {
      price: Math.min(...minimums),
      unit: "minimum",
      hourlyPrice: lowestHourly,
    };
  }
  if (lowestHourly != null) {
    return { price: lowestHourly, unit: "hour", hourlyPrice: null };
  }
  if (car.rentalEventActive && positive(car.priceEvent)) {
    return { price: car.priceEvent, unit: "day", hourlyPrice: null };
  }
  return null;
}

export function isWeddingSuitableCar(car: WeddingSuitabilityCar): boolean {
  return isWeddingVehicleYearEligible(car) && getWeddingDisplayPrice(car) !== null;
}

export function compareWeddingCars<
  T extends Pick<WeddingSuitabilityCar, "year"> & { id: number },
>(a: T, b: T): number {
  return (a.year as number) - (b.year as number) || a.id - b.id;
}